import React, { useState, useMemo } from 'react';
import { T, TRADER_TYPES, TRADER_COLORS, fmtMoney, fmtNum, safeNum, fmtMonth, CHART_COLORS, calcHealthScore } from '../theme';
import { getGroupConfig } from '../riskConfig';
import { Card, DataTable, StatCard, RiskBadge, StatusBadge, HealthBar, MiniBar, CustomTooltip, CoBadge } from '../components/UI';
import { ArrowLeft, Users, TrendingUp, Shield, Award, Calendar, DollarSign, TrendingDown, Zap, Target, Search } from 'lucide-react';
import { BarChart, Bar, AreaChart, Area, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const MNAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

export default function AMDashboard({ C, selectedAM, setSelectedAM, onClientClick, filterYear, setFilterYear, filterMonths, setFilterMonths }) {
  const [amDnaFilter, setAmDnaFilter] = useState(null);
  const [amAlertFilter, setAmAlertFilter] = useState("all");
  const [amSearch, setAmSearch] = useState("");

  const availYears = [...new Set((C.allMonths || []).map(m => String(m).slice(0, 4)))].sort();
  const availMonthNums = (C.allMonths || [])
    .filter(m => filterYear === "all" || String(m).startsWith(filterYear))
    .map(m => parseInt(String(m).slice(4)));
  const toggleMonth = (num) => {
    setFilterMonths(prev => prev.includes(num) ? prev.filter(n => n !== num) : [...prev, num]);
  };

  /* ═══ DATE FILTER BAR ═══ */
  const DateFilterBar = (
    <div style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 12, padding: "12px 16px", marginBottom: 16, display: "flex", alignItems: "center", gap: 14 }}>
      <Calendar size={15} color={T.cyan} style={{ flexShrink: 0 }} />
      <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
        <button onClick={() => { setFilterYear("all"); setFilterMonths([]); }}
          style={{ padding: "5px 12px", borderRadius: 6, border: "none", cursor: "pointer", fontSize: 11, fontWeight: 700,
            background: filterYear === "all" ? T.cyan : T.bg, color: filterYear === "all" ? T.bgCard : T.textMuted }}>All</button>
        {availYears.map(y => (
          <button key={y} onClick={() => { setFilterYear(y); setFilterMonths([]); }}
            style={{ padding: "5px 12px", borderRadius: 6, border: "none", cursor: "pointer", fontSize: 11, fontWeight: 700,
              background: filterYear === y ? T.cyan : T.bg, color: filterYear === y ? T.bgCard : T.textMuted }}>{y}</button>
        ))}
      </div>
      <div style={{ width: 1, height: 20, background: T.border, flexShrink: 0 }} />
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
        {MNAMES.map((name, i) => {
          const num = i + 1;
          const hasData = availMonthNums.includes(num);
          const sel = filterMonths.includes(num);
          const allSel = filterMonths.length === 0;
          return (
            <button key={num} onClick={() => hasData && toggleMonth(num)} disabled={!hasData}
              style={{ padding: "4px 10px", borderRadius: 5, border: "none", fontSize: 10, fontWeight: 600, cursor: hasData ? "pointer" : "default",
                background: sel ? T.cyan : T.bg, color: sel ? T.bgCard : (allSel && hasData ? `${T.cyan}99` : T.textMuted),
                opacity: hasData ? 1 : 0.3 }}>{name}</button>
          );
        })}
      </div>

      {/* Clear filter */}
      {(filterYear !== "all" || filterMonths.length > 0) && (
        <button onClick={() => { setFilterYear("all"); setFilterMonths([]); }}
          style={{ padding: "5px 12px", borderRadius: 6, border: `1px solid ${T.border}`, background: "transparent", color: T.textMuted, cursor: "pointer", fontSize: 10, fontWeight: 600, flexShrink: 0, whiteSpace: "nowrap" }}>
          ✕ Clear
        </button>
      )}
    </div>
  );

  /* ═══ CARD RENDERER ═══ */
  const renderCard = (card) => (
    <div key={card.label} style={{ background: T.bgCard, borderRadius: 12, border: `1px solid ${T.border}`, padding: "14px 12px 12px", position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, ${card.color}, transparent)` }} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <div style={{ fontSize: 11, color: card.color, textTransform: "uppercase", letterSpacing: 0.8, fontWeight: 700 }}>{card.label}</div>
        {card.icon && <span style={{ color: card.color, opacity: 0.7 }}>{card.icon}</span>}
      </div>
      <div className="mono" style={{ fontSize: 20, fontWeight: 800, color: String(card.value).includes("-") ? T.red : T.text, marginBottom: card.total !== undefined ? 4 : 0 }}>{card.value}</div>
      {card.total !== undefined && (
        <div style={{ fontSize: 12, color: T.textDim }}>
          Total: <span style={{ color: String(card.total).includes("-") ? T.red : T.text, fontWeight: 700 }}>{card.total}</span>
        </div>
      )}
      {card.sub && <div style={{ fontSize: 11, color: T.textDim, marginTop: 2 }}>{card.sub}</div>}
    </div>
  );

  // ═══════════════════════════════════════════
  // AM DETAIL VIEW
  // ═══════════════════════════════════════════
  if (selectedAM) {
    const am = C.amStats.find(a => a.name === selectedAM);
    if (!am) return <div style={{ color: T.textDim }}>AM not found</div>;

    const amClients = C.healthScores.filter(c => c["A.M"] === selectedAM);
    const activeClients = amClients.filter(c => c.ClientStatus === "Active");
    const avgHealth = amClients.length > 0 ? Math.round(amClients.reduce((s, c) => s + c.healthScore, 0) / amClients.length) : 0;
    const dormantClients = amClients.filter(c => c.ClientStatus === "Dormant").length;
    const notActiveClients = amClients.filter(c => c.ClientStatus === "Not Active").length;

    // All-time latest clients for this AM (unfiltered - for totals and client counts)
    const amAllClients = (C.latestAllClients || []).filter(c => c["A.M"] === selectedAM);
    const allTotal = amAllClients.length;
    const allActive = amAllClients.filter(c => c.ClientStatus === "Active").length;
    const allDormant = amAllClients.filter(c => c.ClientStatus === "Dormant").length;
    const allNotActive = amAllClients.filter(c => c.ClientStatus === "Not Active").length;

    // Filtered values (current filtered month)
    const fDeposit = amClients.reduce((s, c) => s + safeNum(c.Deposit), 0);
    const fWithdraw = Math.abs(amClients.reduce((s, c) => s + safeNum(c.Withdraw), 0));
    const fVolume = amClients.reduce((s, c) => s + safeNum(c.Volume), 0);
    const fProfit = amClients.reduce((s, c) => s + safeNum(c.Profit), 0);
    const fEquity = amClients.reduce((s, c) => s + safeNum(c.Equity), 0);
    const fCredit = amClients.reduce((s, c) => s + safeNum(c.Credit), 0);
    const fTrades = amClients.reduce((s, c) => s + safeNum(c.TotalTrades || c.TotalDeals), 0);

    // Total values from ALL-TIME latest (unfiltered T.xxx)
    const tDeposit = amAllClients.reduce((s, c) => s + safeNum(c["T.Deposit"]), 0);
    const tWithdraw = Math.abs(amAllClients.reduce((s, c) => s + safeNum(c["T.Withdraw"]), 0));
    const tVolume = amAllClients.reduce((s, c) => s + safeNum(c["T.Volume"]), 0);
    const tProfit = amAllClients.reduce((s, c) => s + safeNum(c["T.Profit"]), 0);
    const tCredit = amAllClients.reduce((s, c) => s + safeNum(c["T.Credit"]), 0);
    const tTrades = (C.allData || []).filter(r => r["A.M"] === selectedAM).reduce((s, r) => s + safeNum(r.TotalTrades || r.TotalDeals), 0);

    // Status breakdown
    const statusData = [
      { name: "Active", value: activeClients.length },
      { name: "Dormant", value: dormantClients },
      { name: "Not Active", value: notActiveClients },
    ].filter(d => d.value > 0);

    // Monthly trend
    const monthlyData = C.months.map(m => {
      const key = selectedAM + "|" + m;
      const d = C.amMonthly[key] || { volume: 0, deposit: 0, active: 0, clients: 0 };
      return { month: fmtMonth(m), ...d };
    });

    // Health distribution
    const healthDist = { safe: 0, low: 0, elevated: 0, high: 0, critical: 0 };
    amClients.forEach(c => {
      if (c.healthScore >= 51) healthDist.critical++;
      else if (c.healthScore >= 39) healthDist.high++;
      else if (c.healthScore >= 27) healthDist.elevated++;
      else if (c.healthScore >= 16) healthDist.low++;
      else healthDist.safe++;
    });

    return (
      <div>
        <button onClick={() => setSelectedAM(null)} style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "none", color: T.cyan, cursor: "pointer", fontSize: 13, marginBottom: 16 }}>
          <ArrowLeft size={16} /> Back
        </button>

        <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 20, display: "flex", alignItems: "center", gap: 8 }}>
          {(am._companies || []).map(co => <CoBadge key={co} co={co} size={22} />)}
          {am.name} <span style={{ fontSize: 14, color: "#8494a7", fontWeight: 400 }}>{allTotal} clients • {allActive} active</span>
        </h1>

        {DateFilterBar}

        {/* Row 1: 6 cards */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 10, marginBottom: 10 }}>
          {[
            { label: "Total Clients", value: allTotal.toLocaleString(), color: T.cyan, icon: <Users size={18} /> },
            { label: "Active", value: allActive.toLocaleString(), color: T.green, icon: <TrendingUp size={18} />, sub: `${allTotal > 0 ? (allActive / allTotal * 100).toFixed(0) : 0}%` },
            { label: "Dormant", value: allDormant.toLocaleString(), color: T.amber, sub: `${allTotal > 0 ? (allDormant / allTotal * 100).toFixed(1) : 0}%` },
            { label: "Not Active", value: allNotActive.toLocaleString(), color: T.red, sub: `${allTotal > 0 ? (allNotActive / allTotal * 100).toFixed(1) : 0}%` },
            { label: "Volume", value: fmtNum(fVolume, 1), color: T.amber, icon: <Zap size={18} />, total: fmtNum(tVolume, 1) },
            { label: "Trades", value: fTrades.toLocaleString(), color: T.blue, icon: <Target size={18} />, total: tTrades.toLocaleString() },
          ].map(renderCard)}
        </div>

        {/* Row 2: 5 cards */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 10, marginBottom: 24 }}>
          {[
            { label: "Profit", value: fmtMoney(fProfit), color: fProfit >= 0 ? T.green : T.red, icon: <Target size={18} />, total: fmtMoney(tProfit) },
            { label: "Deposit", value: fmtMoney(fDeposit), color: T.green, icon: <DollarSign size={18} />, total: fmtMoney(tDeposit) },
            { label: "Withdraw", value: fmtMoney(fWithdraw), color: T.red, icon: <TrendingDown size={18} />, total: fmtMoney(tWithdraw) },
            { label: "Equity", value: fmtMoney(fEquity), color: T.cyan, icon: <DollarSign size={18} /> },
            { label: "Credit", value: fmtMoney(fCredit), color: T.purple, icon: <Zap size={18} />, total: fmtMoney(tCredit) },
          ].map(renderCard)}
        </div>

        {/* Row 3: Trader DNA + Risk Distribution */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
          <Card accent={T.amber} style={{ display: "flex", flexDirection: "column" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexShrink: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 600 }}>Trader DNA</div>
              {amDnaFilter && <button onClick={() => setAmDnaFilter(null)} style={{ fontSize: 10, padding: "3px 10px", borderRadius: 4, border: `1px solid ${T.border}`, background: T.bgCard, color: T.textMuted, cursor: "pointer", fontWeight: 600 }}>✕ Show All</button>}
            </div>
            {!amDnaFilter ? (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                {(() => {
                  const types = [
                    { key: "Scalper", label: "Scalper", color: TRADER_COLORS.Scalper },
                    { key: "Hedger", label: "Hedger", color: TRADER_COLORS.Hedger },
                    { key: "HedgingAbuser", label: "Hedging Abuser", color: TRADER_COLORS.HedgingAbuser },
                    { key: "Commissioner", label: "Commissioner", color: TRADER_COLORS.Commissioner },
                    { key: "NewsTrader", label: "News Trader", color: TRADER_COLORS.NewsTrader },
                    { key: "HighFrequencyTrader", label: "High Frequency Trader", color: TRADER_COLORS.HighFrequencyTrader },
                    { key: "HeavyTrader", label: "Heavy Trader", color: TRADER_COLORS.HeavyTrader },
                    { key: "Swaper", label: "Swaper", color: TRADER_COLORS.Swaper },
                    { key: "GainerTrader", label: "Gainer Trader", color: TRADER_COLORS.GainerTrader },
                    { key: "ArbitrageSuspected", label: "Arbitrage Suspected", color: "#ff0055" },
                  ];
                  const arbCfg = getGroupConfig().arbitrage;
                  const isArb = c => { const cds = (arbCfg.conditions||[]).filter(x=>x.enabled); if(!cds.length) return false; return cds.every(cd => { const v=safeNum(c[cd.column]); if(cd.op==="flag") return String(c[cd.column])===cd.value; if(cd.op===">=") return v>=cd.value; if(cd.op==="<") return v>0&&v<cd.value; if(cd.op===">") return v>cd.value; return false; }); };
                  const counts = types.map(t => ({ ...t, count: t.key === "ArbitrageSuspected" ? amClients.filter(isArb).length : amClients.filter(c => c[t.key] === t.key).length })).filter(d => d.count > 0).sort((a, b) => b.count - a.count);
                  const total = counts.reduce((s, d) => s + d.count, 0);
                  return counts.map((d, i) => (
                    <div key={i} onClick={() => setAmDnaFilter(d.key)} style={{ background: T.bg, borderRadius: 10, padding: "18px 16px", cursor: "pointer", borderLeft: `4px solid ${d.color}`, transition: "all 0.15s" }}
                      onMouseEnter={e => { e.currentTarget.style.background = T.bgHover; e.currentTarget.style.transform = "scale(1.02)"; }}
                      onMouseLeave={e => { e.currentTarget.style.background = T.bg; e.currentTarget.style.transform = "scale(1)"; }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ fontSize: 13, color: d.color, fontWeight: 700 }}>{d.label}</span>
                        <span className="mono" style={{ fontSize: 22, fontWeight: 800, color: T.text }}>{d.count}</span>
                      </div>
                      <div style={{ fontSize: 11, color: T.textDim, marginTop: 6 }}>{total > 0 ? (d.count / total * 100).toFixed(1) : 0}% of flagged</div>
                    </div>
                  ));
                })()}
              </div>
            ) : (
              <div style={{ overflowY: "auto", flex: 1 }}>
                {(() => {
                  const arbCfg2 = getGroupConfig().arbitrage;
                  const isArb2 = c => { const cds = (arbCfg2.conditions||[]).filter(x=>x.enabled); if(!cds.length) return false; return cds.every(cd => { const v=safeNum(c[cd.column]); if(cd.op==="flag") return String(c[cd.column])===cd.value; if(cd.op===">=") return v>=cd.value; if(cd.op==="<") return v>0&&v<cd.value; if(cd.op===">") return v>cd.value; return false; }); };
                  const fl = amDnaFilter === "ArbitrageSuspected" ? amClients.filter(isArb2) : amClients.filter(c => c[amDnaFilter] === amDnaFilter);
                  const dn = amDnaFilter.replace(/([A-Z])/g, ' $1').trim();
                  return <>
                    <div style={{ fontSize: 13, color: T.amber, fontWeight: 600, marginBottom: 10 }}>{dn} — {fl.length} clients</div>
                    {fl.slice(0, 50).map((c, i) => (
                      <div key={i} onClick={() => onClientClick(c)} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: `1px solid ${T.border}15`, cursor: "pointer", fontSize: 12 }}
                        onMouseEnter={e => e.currentTarget.style.background = T.bgHover} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <CoBadge co={c._company} />
                          <span style={{ color: T.text }}>{(c.Name || c.Login + "").substring(0, 22)}</span>
                        </div>
                        <span className="mono" style={{ color: T.textDim, fontSize: 10 }}>{c.Login}</span>
                      </div>
                    ))}
                  </>;
                })()}
              </div>
            )}
          </Card>
          <Card accent={T.red} style={{ display: "flex", flexDirection: "column", maxHeight: 380 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <div>
                <div style={{ fontSize: 13, color: T.amber, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700, marginBottom: 2 }}>Risk Distribution</div>
                <div style={{ fontSize: 11, color: T.textMuted }}>Broker Risk Score</div>
              </div>
              <Shield size={20} color={T.amber} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", justifyContent: "space-between", flex: 1 }}>
              {[
                { label: "Critical (51+)", count: healthDist.critical, color: T.red },
                { label: "High (39-50)", count: healthDist.high, color: T.orange },
                { label: "Elevated (27-38)", count: healthDist.elevated, color: T.amber },
                { label: "Low (16-26)", count: healthDist.low, color: T.cyan },
                { label: "Safe (0-15)", count: healthDist.safe, color: T.green },
              ].map(h => (
                <div key={h.label} style={{ marginBottom: 2 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, marginBottom: 5 }}>
                    <span style={{ color: h.color, fontWeight: 700 }}>{h.label}</span>
                    <span style={{ color: T.text, fontWeight: 800, fontSize: 16 }}>{h.count} <span style={{ color: T.textDim, fontWeight: 400, fontSize: 12 }}>({(h.count / Math.max(amClients.length, 1) * 100).toFixed(0)}%)</span></span>
                  </div>
                  <div style={{ height: 10, background: T.bgHover, borderRadius: 5, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: Math.max((h.count / Math.max(amClients.length, 1) * 100), h.count > 0 ? 1 : 0) + "%", background: h.color, borderRadius: 5 }} />
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Row 4: Volume Trend + Alerts */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 24 }}>
          <Card accent={T.green}>
            <div style={{ fontSize: 14, fontWeight: 600, color: T.text, marginBottom: 12 }}>Volume Trend</div>
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={monthlyData}>
                <defs>
                  <linearGradient id="amVolGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={T.green} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={T.green} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={T.border} />
                <XAxis dataKey="month" tick={{ fill: T.textMuted, fontSize: 9 }} />
                <YAxis tick={{ fill: T.textMuted, fontSize: 9 }} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="volume" stroke={T.green} strokeWidth={2.5} fill="url(#amVolGrad)" name="Volume" dot={{ r: 3, fill: T.green }} />
              </AreaChart>
            </ResponsiveContainer>
          </Card>
          {(() => {
            const amLogins = new Set(amClients.map(c => c._company + "|" + c.Login));
            const allAmAlerts = (C.alerts || []).filter(a => amLogins.has(a._company + "|" + a.login));
            const amAlerts = amAlertFilter === "all" ? allAmAlerts : allAmAlerts.filter(a => a.type === amAlertFilter);
            return (
              <Card accent={T.red} style={{ display: "flex", flexDirection: "column", maxHeight: 310 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, paddingBottom: 8, borderBottom: `1px solid ${T.border}30`, flexShrink: 0 }}>
                  <span style={{ fontSize: 14 }}>🔔</span>
                  <span style={{ fontSize: 14, fontWeight: 600 }}>Alerts</span>
                  <span style={{ fontSize: 11, color: T.red, fontWeight: 700, background: T.redBg, padding: "2px 8px", borderRadius: 10 }}>{amAlerts.length}</span>
                  <div style={{ marginLeft: "auto", display: "flex", gap: 4 }}>
                    {[
                      { key: "all", label: "All", color: T.text },
                      { key: "critical", label: "🔴 Flags", color: T.red },
                      { key: "high", label: "🟠 Equity", color: T.orange },
                      { key: "medium", label: "🟡 Group", color: T.amber },
                    ].map(f => (
                      <button key={f.key} onClick={() => setAmAlertFilter(f.key)}
                        style={{ fontSize: 10, padding: "3px 8px", borderRadius: 4, cursor: "pointer", fontWeight: 600, border: "none",
                          background: amAlertFilter === f.key ? (f.key === "all" ? T.bgHover : `${f.color}25`) : "transparent",
                          color: amAlertFilter === f.key ? f.color : T.textMuted }}>
                        {f.label}
                      </button>
                    ))}
                  </div>
                </div>
                {amAlerts.length === 0 ? (
                  <div style={{ color: T.green, fontSize: 13, textAlign: "center", padding: 30, flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>No alerts ✓</div>
                ) : (
                  <div style={{ overflowY: "auto", flex: 1 }}>
                    {amAlerts.map((a, i) => (
                      <div key={i} onClick={() => onClientClick(amClients.find(c => c.Login === a.login && c._company === a._company))}
                        style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", marginBottom: 4, borderRadius: 8, cursor: "pointer",
                          background: a.type === "critical" ? T.redBg : a.type === "high" ? T.orangeBg : T.yellowBg }}
                        onMouseEnter={e => e.currentTarget.style.opacity = "0.8"} onMouseLeave={e => e.currentTarget.style.opacity = "1"}>
                        <span style={{ fontSize: 12 }}>{a.icon}</span>
                        <CoBadge co={a._company} />
                        <span style={{ fontSize: 12, color: T.text, fontWeight: 600, flex: 1 }}>{a.name}</span>
                        <div style={{ display: "flex", gap: 4, flexWrap: "wrap", justifyContent: "flex-end" }}>
                          {a.flags.map((f, j) => (
                            <span key={j} style={{ fontSize: 9, padding: "2px 6px", borderRadius: 4, fontWeight: 600,
                              background: a.type === "critical" ? `${T.red}25` : a.type === "high" ? `${T.orange}25` : `${T.amber}25`,
                              color: a.type === "critical" ? T.red : a.type === "high" ? T.orange : T.amber }}>{f}</span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            );
          })()}
        </div>

        {/* Client List */}
        <Card>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>Clients</div>
            <div style={{ flex: 1, position: "relative" }}>
              <Search size={16} color={T.textDim} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }} />
              <input value={amSearch} onChange={e => setAmSearch(e.target.value)}
                placeholder="Search by Login, Name, or CU..."
                style={{ width: "100%", padding: "10px 12px 10px 38px", borderRadius: 8, border: `1px solid ${T.border}`, background: T.bgInput || T.bg, color: T.text, fontSize: 13, outline: "none" }} />
            </div>
            <span className="mono" style={{ fontSize: 12, color: T.textDim, whiteSpace: "nowrap" }}>
              {(() => { const s = amSearch.trim().toLowerCase(); const f = s ? amClients.filter(c => String(c.Login||"").toLowerCase().includes(s) || String(c.Name||"").toLowerCase().includes(s) || String(c.CU_Account||"").toLowerCase().includes(s)) : amClients; return f.length; })()} clients
            </span>
          </div>
          <DataTable exportTitle="AM-Clients" pageSize={15} onRowClick={onClientClick}
            columns={[
              { label: "Login", key: "Login", render: (v, row) => <span className="mono" style={{ display: "flex", alignItems: "center", gap: 6, color: "#06b6d4" }}><CoBadge co={row._company} size={16} />{v}</span> },
              { label: "Name", render: r => <span style={{ color: T.text }}>{(r.Name || "—").substring(0, 22)}</span> },
              { label: "Status", render: r => <StatusBadge status={r.ClientStatus} /> },
              { label: "Risk", render: r => <RiskBadge level={r.BehavioralRisk} /> },
              { label: "Risk Score", value: r => r.healthScore, render: r => <HealthBar score={r.healthScore} /> },
              { label: "Equity", value: r => safeNum(r.Equity), render: r => fmtMoney(safeNum(r.Equity)), align: "right" },
              { label: "Volume", value: r => safeNum(r.Volume), render: r => fmtNum(safeNum(r.Volume)), align: "right" },
            ]}
            data={(() => { const s = amSearch.trim().toLowerCase(); return s ? amClients.filter(c => String(c.Login||"").toLowerCase().includes(s) || String(c.Name||"").toLowerCase().includes(s) || String(c.CU_Account||"").toLowerCase().includes(s)) : amClients; })()} />
        </Card>
      </div>
    );
  }

  // ═══════════════════════════════════════════
  // LEADERBOARD VIEW
  // ═══════════════════════════════════════════
  const maxVol = Math.max(...C.amStats.map(a => a.volume), 1);
  const chartData = C.amStats.filter(a => a.name !== "closed closed" && a.name !== "System Administrator").slice(0, 10);

  return (
    <div>
      <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 20 }}>Account Managers</h1>

      {DateFilterBar}

      {/* Summary Cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 24 }}>
        <StatCard label="Total Account Managers" value={C.amStats.filter(a => a.active > 0).length} sub="With active clients" icon={<Users size={22} />} color={T.cyan} />
        <StatCard label="Avg Clients/AM" value={Math.round(C.amStats.reduce((s, a) => s + a.clients, 0) / Math.max(C.amStats.length, 1))} color={T.blue} />
        <StatCard label="Top Volume" value={C.amStats[0]?.name?.split(" ")[0] || "—"} sub={fmtNum(C.amStats[0]?.volume || 0) + " lots"} icon={<Award size={22} />} color={T.green} />
        <StatCard label="Top Deposits" value={(() => { const s = [...C.amStats].sort((a, b) => b.deposit - a.deposit); return s[0]?.name?.split(" ")[0] || "—"; })()} sub={fmtMoney((() => { const s = [...C.amStats].sort((a, b) => b.deposit - a.deposit); return s[0]?.deposit || 0; })())} color={T.purple} />
      </div>

      {/* Charts */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 24 }}>
        <Card accent={T.cyan}>
          <div style={{ fontSize: 13, fontWeight: 600, color: T.text, marginBottom: 12 }}>Volume by Account Manager</div>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={chartData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke={T.border} />
              <XAxis type="number" tick={{ fill: T.textMuted, fontSize: 9 }} />
              <YAxis dataKey="name" type="category" width={120} tick={{ fill: T.textDim, fontSize: 10 }} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="volume" fill={T.cyan} name="Volume" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card accent={T.green}>
          <div style={{ fontSize: 13, fontWeight: 600, color: T.text, marginBottom: 12 }}>Active Clients by Account Manager</div>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={chartData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke={T.border} />
              <XAxis type="number" tick={{ fill: T.textMuted, fontSize: 9 }} />
              <YAxis dataKey="name" type="category" width={120} tick={{ fill: T.textDim, fontSize: 10 }} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="active" fill={T.green} name="Active" radius={[0, 4, 4, 0]} />
              <Bar dataKey="highRisk" fill={T.red} name="High Risk" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Leaderboard Table */}
      <Card>
        <div style={{ fontSize: 15, fontWeight: 700, color: T.text, marginBottom: 14 }}>Account Manager Leaderboard</div>
        <DataTable exportTitle="Account-Managers" pageSize={20} fixedLayout onRowClick={r => setSelectedAM(r.name)}
          columns={[
            { label: "Account Manager", width: "16%", render: r => <span style={{ display: "flex", alignItems: "center", gap: 6, color: T.text, fontWeight: 600, fontSize: 13 }}>{(r._companies || []).map(co => <CoBadge key={co} co={co} size={16} />)}{r.name}</span> },
            { label: "Clients", width: "7%", value: r => r.clients, render: r => <span className="mono">{r.clients.toLocaleString()}</span>, align: "center" },
            { label: "Active", width: "6%", value: r => r.active, render: r => <span className="mono" style={{ color: T.green, fontWeight: 600 }}>{r.active}</span>, align: "center" },
            { label: "Active %", width: "6%", value: r => r.clients > 0 ? r.active / r.clients : 0, render: r => <span style={{ color: r.active / r.clients > 0.15 ? T.green : T.amber, fontWeight: 600 }}>{(r.active / r.clients * 100).toFixed(0)}%</span>, align: "center" },
            { label: "High Risk", width: "6%", value: r => r.highRisk, render: r => r.highRisk > 0 ? <span style={{ color: T.red, fontWeight: 700 }}>{r.highRisk}</span> : <span style={{ color: T.textMuted }}>0</span>, align: "center" },
            ...["Scalper","Hedger","HedgingAbuser","Commissioner","NewsTrader","HighFrequencyTrader","HeavyTrader","Swaper","GainerTrader","ArbitrageSuspected"].map(t => ({
              label: t === "HighFrequencyTrader" ? "HFT" : t === "HedgingAbuser" ? "Hedge Ab." : t === "NewsTrader" ? "News Tr." : t === "HeavyTrader" ? "Heavy Tr." : t === "GainerTrader" ? "Gainer" : t === "ArbitrageSuspected" ? "Arb." : t,
              width: "6%",
              value: r => r[t] || 0,
              render: r => {
                const cnt = r[t] || 0;
                const col = TRADER_COLORS[t];
                return cnt > 0 ? <span style={{ color: col, fontWeight: 700 }}>{cnt}</span> : <span style={{ color: T.textMuted + "50" }}>0</span>;
              },
              align: "center"
            })),
          ]}
          data={C.amStats} />
      </Card>
    </div>
  );
}
